/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import type { SoundingsCollection } from '@opengeoweb/shared';
import type { CoreAppStore } from '@opengeoweb/store';
import {
  mapActions,
  layerActions,
  genericSelectors,
  uiSelectors,
  uiTypes,
  useSetupDialog,
} from '@opengeoweb/store';
import { LocationsMenu } from './LocationsMenu';
import { useGetLocations } from '../../api/edr-api/hooks';
import { soundingsActions } from '../../store/reducer';
import {
  getLocationMenuPosition,
  getLocationMenuSize,
  getLocationSelections,
  getSelectedSourcesAmount,
  getSelectedSourcesIds,
  getSoundingsCollections,
} from '../../store/selectors';
import type { SoundingsRootState } from '../../store/types';
import { SoundingsActionOrigin } from '../../store/types';

interface Props {
  soundingsCollections?: SoundingsCollection[];
}

export const LocationsMenuConnect = ({
  soundingsCollections,
}: Props): React.ReactElement => {
  const dispatch = useDispatch();
  const { isDialogOpen, onCloseDialog, dialogOrder, setDialogOrder } =
    useSetupDialog(uiTypes.DialogTypes.SoundingsLocations);

  const viewId = useSelector<CoreAppStore, string | undefined>((store) =>
    uiSelectors.getDialogMapId(store, uiTypes.DialogTypes.SoundingsLocations),
  );
  const locationSelections = useSelector((state: SoundingsRootState) =>
    getLocationSelections(state, viewId),
  );
  const selectedSourceIds = useSelector((state: SoundingsRootState) =>
    getSelectedSourcesIds(state, viewId),
  );
  const sourcesAmount = useSelector((state: SoundingsRootState) =>
    getSelectedSourcesAmount(state, viewId),
  );
  const soundingsCollectionsFromStore = useSelector(
    (state: SoundingsRootState) => getSoundingsCollections(state),
  );
  const collections = soundingsCollections ?? soundingsCollectionsFromStore;
  const selectedMapIds = useSelector(
    (state: CoreAppStore) =>
      viewId ? genericSelectors.getLinkedTargetIds(state, viewId) : [],
    shallowEqual,
  );
  const ownedFeatureLayerIds = useSelector(
    (state: CoreAppStore) =>
      viewId
        ? genericSelectors.selectFeatureSyncLayerIdsForPanel(state, viewId)
        : [],
    shallowEqual,
  );

  React.useEffect(() => {
    if (soundingsCollections && soundingsCollections.length > 0) {
      dispatch(soundingsActions.setSoundingsCollections(soundingsCollections));
    }
  }, [dispatch, soundingsCollections]);

  const locationMenuSize = useSelector((state: SoundingsRootState) =>
    getLocationMenuSize(state, viewId),
  );

  const locationMenuPosition = useSelector((state: SoundingsRootState) =>
    getLocationMenuPosition(state, viewId),
  );

  const { data, isPending, error } = useGetLocations(collections, isDialogOpen);

  const services = useMemo(() => {
    return (
      data?.filter((service) => selectedSourceIds.includes(service.id)) || []
    );
  }, [data, selectedSourceIds]);

  /**
   * Handles location selection from the menu.
   * When a location is selected for one source:
   * 1. Select that location for the chosen source
   * 2. For other sources that had the same location selected, change their
   *    selection to use coordinates (position query) instead
   * 3. Update the map pin and selectedFeature to show a red marker
   */
  const handleUpdateSelectedLocation = useCallback(
    (sourceId: string, locationId?: string) => {
      // Update the location for the selected source
      dispatch(
        soundingsActions.updateSelectedLocation({
          sourceId,
          location: locationId,
          viewId,
          origin: SoundingsActionOrigin.soundingsComponent,
        }),
      );

      ownedFeatureLayerIds.forEach((layerId) => {
        dispatch(
          layerActions.setSelectedFeatures({
            layerId,
            selectedFeatureIds: locationId ? [locationId] : [],
          }),
        );
      });

      // If selecting a location (not deselecting), handle other sources
      if (locationId) {
        // Find the coordinates of the selected location from the source's features
        const sourceService = services.find(
          (service) => service.id === sourceId,
        );
        const selectedFeature = sourceService?.features.find(
          (feature) => feature.id === locationId,
        );

        if (selectedFeature?.geometry.type === 'Point') {
          const [lon, lat] = selectedFeature.geometry.coordinates as [
            number,
            number,
          ];

          // For other sources that have the same location ID selected,
          // change their selection to use coordinates instead
          selectedSourceIds.forEach((otherSourceId) => {
            if (otherSourceId !== sourceId) {
              const otherLocation = locationSelections[otherSourceId];
              if (otherLocation === locationId) {
                dispatch(
                  soundingsActions.updateSelectedLocation({
                    sourceId: otherSourceId,
                    location: [lon, lat],
                    viewId,
                    origin: SoundingsActionOrigin.soundingsComponent,
                  }),
                );
              }
            }
          });

          // Update map pin to show the selected location with red marker
          selectedMapIds.forEach((mapId) => {
            dispatch(
              mapActions.setMapPinLocation({
                mapId,
                mapPinLocation: { lon, lat, id: locationId },
              }),
            );
          });
        }
      }
    },
    [
      dispatch,
      viewId,
      services,
      selectedSourceIds,
      locationSelections,
      selectedMapIds,
      ownedFeatureLayerIds,
    ],
  );

  const handleSizeChange = useCallback(
    (
      size: { width: number; height: number },
      position: { x: number; y: number },
    ): void => {
      dispatch(
        soundingsActions.setLocationMenuSizeAndPosition({
          size,
          position,
          viewId,
          origin: SoundingsActionOrigin.soundingsComponent,
        }),
      );
    },
    [dispatch, viewId],
  );

  const handleClose = useCallback((): void => {
    onCloseDialog();
    dispatch(
      soundingsActions.setLocationsMenuOpen({
        viewId,
        isLocationsDialogOpen: false,
        origin: SoundingsActionOrigin.soundingsComponent,
      }),
    );
  }, [dispatch, onCloseDialog, viewId]);

  return (
    <LocationsMenu
      error={error}
      isLoading={isPending}
      isOpen={isDialogOpen}
      locationMenuPosition={locationMenuPosition}
      locationMenuSize={locationMenuSize}
      onClose={handleClose}
      onMouseDown={setDialogOrder}
      onSizeChange={handleSizeChange}
      order={dialogOrder}
      selectedLocations={locationSelections}
      selectedSourcesAmount={sourcesAmount}
      services={services}
      updateSelectedLocation={handleUpdateSelectedLocation}
    />
  );
};
